import { useState, useCallback, useRef } from 'react'
import * as THREE from 'three'
import { Html } from '@react-three/drei'

export function useGameController() {
  const [gameState, setGameState] = useState({
    currentTeam: 1,
    team1Moves: 12,
    team2Moves: 12,
    team1Score: 0,
    team2Score: 0,
    gameOver: false,
    placedShapes: []
  })
  
  const placedShapesRef = useRef([])
  // CAGE CONFIG - matches Scene.js DEFAULT_DEBUG_CONFIG
  // User's preferred settings (Jan 2026)
  const CAGE_CENTER = { x: 0, y: 45, z: 0 }
  const CAGE_SIZE = { x: 41, y: 50, z: 24 }
  const boundaryWidth = CAGE_SIZE.x    // 41
  const boundaryHeight = CAGE_SIZE.y   // 50
  const boundaryDepth = CAGE_SIZE.z    // 24
  const gameBoxY = CAGE_CENTER.y - CAGE_SIZE.y / 2  // Bottom of cage: 20
  
  const checkCollision = useCallback((position, scale = 1.0) => {
    const padding = 0.5
    const shapeRadius = 2.0 * scale
    
    const newBox = new THREE.Box3(
      new THREE.Vector3(
        position.x - shapeRadius - padding,
        position.y - shapeRadius - padding,
        position.z - shapeRadius - padding
      ),
      new THREE.Vector3(
        position.x + shapeRadius + padding,
        position.y + shapeRadius + padding,
        position.z + shapeRadius + padding
      )
    )
    
    const boundaryBox = new THREE.Box3(
      new THREE.Vector3(-boundaryWidth/2, gameBoxY, -boundaryDepth/2),
      new THREE.Vector3(boundaryWidth/2, gameBoxY + boundaryHeight, boundaryDepth/2)
    )
    
    if (!boundaryBox.containsBox(newBox)) {
      return true
    }
    
    for (let shape of placedShapesRef.current) {
      const existingBox = new THREE.Box3(
        new THREE.Vector3(
          shape.position[0] - shapeRadius - padding,
          shape.position[1] - shapeRadius - padding,
          shape.position[2] - shapeRadius - padding
        ),
        new THREE.Vector3(
          shape.position[0] + shapeRadius + padding,
          shape.position[1] + shapeRadius + padding,
          shape.position[2] + shapeRadius + padding
        )
      )
      
      if (newBox.intersectsBox(existingBox)) {
        return true
      }
    }
    
    return false
  }, [])
  
  const findValidPosition = useCallback((team, maxAttempts = 100) => {
    const margin = 5  // Define margin first
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Position shapes more precisely within cage bounds (tighter spread)
      const spreadFactorXZ = 0.4  // Horizontal spread
      
      const x = CAGE_CENTER.x + (Math.random() - 0.5) * (boundaryWidth * spreadFactorXZ)
      // Y positioning: use full cage height more evenly (from bottom to top)
      const y = gameBoxY + margin + Math.random() * (boundaryHeight - margin * 2)
      const z = CAGE_CENTER.z + (Math.random() - 0.5) * (boundaryDepth * spreadFactorXZ)
      
      // Clamp to ensure within cage boundaries with margin
      const clampedX = Math.max(-boundaryWidth/2 + margin, Math.min(boundaryWidth/2 - margin, x))
      const clampedY = Math.max(gameBoxY + margin, Math.min(gameBoxY + boundaryHeight - margin, y))
      const clampedZ = Math.max(-boundaryDepth/2 + margin, Math.min(boundaryDepth/2 - margin, z))
      
      const position = new THREE.Vector3(clampedX, clampedY, clampedZ)
      
      if (!checkCollision(position, 0.3)) {
        return [clampedX, clampedY, clampedZ]
      }
    }
    
    return null
  }, [checkCollision, boundaryWidth, boundaryHeight, boundaryDepth, gameBoxY])
  
  const calculateProjections = useCallback(() => {
    const resolution = 40
    const faces = [
      { axis: 'x', value: boundaryWidth/2 },
      { axis: 'x', value: -boundaryWidth/2 },
      { axis: 'y', value: gameBoxY + boundaryHeight },
      { axis: 'y', value: gameBoxY },
      { axis: 'z', value: boundaryDepth/2 },
      { axis: 'z', value: -boundaryDepth/2 }
    ]
    
    let team1Area = 0
    let team2Area = 0
    const totalArea = resolution * resolution * 6
    
    faces.forEach(face => {
      for (let i = 0; i < resolution; i++) {
        for (let j = 0; j < resolution; j++) {
          const centerY = gameBoxY + boundaryHeight / 2
          const maxDim = Math.max(boundaryWidth, boundaryDepth)
          const uScaled = (i / resolution - 0.5) * maxDim
          const vScaled = (j / resolution - 0.5) * maxDim
          
          let point
          
          if (face.axis === 'x') {
            point = new THREE.Vector3(face.value, uScaled + centerY, vScaled)
          } else if (face.axis === 'y') {
            point = new THREE.Vector3(uScaled, face.value, vScaled)
          } else {
            point = new THREE.Vector3(uScaled, vScaled + centerY, face.value)
          }
          
          let minDistance = Infinity
          let closestTeam = 0
          
          for (let shape of placedShapesRef.current) {
            const shapePos = new THREE.Vector3(...shape.position)
            const distance = point.distanceTo(shapePos)
            if (distance < minDistance) {
              minDistance = distance
              closestTeam = shape.team
            }
          }
          
          if (minDistance < boundaryHeight * 0.3) {
            if (closestTeam === 1) team1Area++
            else if (closestTeam === 2) team2Area++
          }
        }
      }
    })
    
    return {
      team1: team1Area,
      team2: team2Area,
      total: totalArea,
      team1Percentage: Math.round(team1Area * 100 / totalArea),
      team2Percentage: Math.round(team2Area * 100 / totalArea)
    }
  }, [])
  
  const placeShape = useCallback((shapeIndex, forceTeam = null) => {
    if (gameState.gameOver) {
      console.log('Game over')
      return false
    }
    
    // Use forced team if provided, otherwise use current team
    const teamToPlace = forceTeam || gameState.currentTeam
    const currentMoves = teamToPlace === 1 ? gameState.team1Moves : gameState.team2Moves
    
    if (currentMoves <= 0) {
      console.log(`No moves left for team ${teamToPlace}`)
      return false
    }
    
    const position = findValidPosition(teamToPlace)
    if (!position) {
      console.log('Could not find valid position')
      return false
    }
    
    // Entry from outside cage - team 1 from left, team 2 from right
    const entryX = teamToPlace === 1 ? -30 : 30
    const newShape = {
      id: `${teamToPlace}-${Date.now()}`,
      team: teamToPlace,
      position: position,
      rotation: [Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI],
      scale: 0.1, // Scale to fit inside cage (shapes are ~50 units, scaled to ~5 units)
      shapeIndex: shapeIndex,
      isAnimating: true,
      animationStartTime: Date.now(),
      entryPosition: [entryX, position[1], position[2]]
    }
    
    placedShapesRef.current.push(newShape)
    console.log('Shape placed at X:', position[0].toFixed(2), 'Y:', position[1].toFixed(2), 'Z:', position[2].toFixed(2), '| Team:', teamToPlace, '| Scale:', newShape.scale)
    
    const newMoves = teamToPlace === 1 
      ? { team1Moves: gameState.team1Moves - 1 }
      : { team2Moves: gameState.team2Moves - 1 }
    
    // Switch to the other team after placing
    const nextTeam = teamToPlace === 1 ? 2 : 1
    const projections = calculateProjections()
    
    const team1MovesLeft = teamToPlace === 1 ? newMoves.team1Moves : gameState.team1Moves
    const team2MovesLeft = teamToPlace === 2 ? newMoves.team2Moves : gameState.team2Moves
    const isGameOver = team1MovesLeft === 0 && team2MovesLeft === 0
    
    setGameState(prev => ({
      ...prev,
      ...newMoves,
      currentTeam: nextTeam,
      team1Score: projections.team1Percentage,
      team2Score: projections.team2Percentage,
      gameOver: isGameOver,
      placedShapes: [...placedShapesRef.current]
    }))
    
    return true
  }, [gameState, findValidPosition, calculateProjections])
  
  const resetGame = useCallback(() => {
    placedShapesRef.current = []
    setGameState({
      currentTeam: 1,
      team1Moves: 12,
      team2Moves: 12,
      team1Score: 0,
      team2Score: 0,
      gameOver: false,
      placedShapes: []
    })
  }, [])
  
  const skipTurn = useCallback(() => {
    if (gameState.gameOver) return
    
    const nextTeam = gameState.currentTeam === 1 ? 2 : 1
    setGameState(prev => ({
      ...prev,
      currentTeam: nextTeam
    }))
  }, [gameState])
  
  return {
    gameState,
    placeShape,
    resetGame,
    skipTurn
  }
}

export function GameUI({ gameState, placeShape, resetGame, skipTurn, onPOVClick, currentView }) {
  const handlePOVClick = (povKey) => {
    onPOVClick(povKey);
  };

  const currentTeamName = gameState.currentTeam === 1 ? 'TOKYO' : 'LA';
  const currentTeamColor = gameState.currentTeam === 1 ? '#FFFFC5' : '#4ecdc4';

  return (
    <>
      <div className="game-ui">
        <div className="game-panel">
          <h3>GAME CONTROLS</h3>
          
          {/* Current Turn Indicator */}
          {!gameState.gameOver && (
            <div className="current-turn-indicator" style={{
              background: `linear-gradient(135deg, ${currentTeamColor}22, ${currentTeamColor}11)`,
              border: `2px solid ${currentTeamColor}`,
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '10px', 
                letterSpacing: '2px', 
                color: '#888',
                marginBottom: '4px'
              }}>CURRENT TURN</div>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                color: currentTeamColor,
                fontFamily: "'Allrounder Monument', 'Orbitron', sans-serif",
                letterSpacing: '3px',
                textShadow: `0 0 10px ${currentTeamColor}66`
              }}>{currentTeamName}</div>
            </div>
          )}
          
          <div className="turn-info">
            <div>TOKYO Moves: {gameState.team1Moves}/12</div>
            <div>LA Moves: {gameState.team2Moves}/12</div>
          </div>
          
          <div className="score-info">
            <div>TOKYO Coverage: {gameState.team1Score}%</div>
            <div>LA Coverage: {gameState.team2Score}%</div>
          </div>
          
          <div className="game-buttons">
            <button onClick={skipTurn} disabled={gameState.gameOver}>
              Skip Turn
            </button>
            <button onClick={resetGame}>
              Reset Game
            </button>
            {currentView !== 'reset' && (
              <button
                className="reset-view-game"
                onClick={() => onPOVClick('reset')}
              >
                Reset View
              </button>
            )}
            
            {currentView === 'newYork' && (
              <button
                className="pov-button tokyo switch-pov"
                onClick={() => onPOVClick('tokyo')}
              >
                → SWITCH TO TOKYO
              </button>
            )}
            {currentView === 'tokyo' && (
              <button
                className="pov-button new-york switch-pov"
                onClick={() => onPOVClick('newYork')}
              >
                → SWITCH TO LA
              </button>
            )}
          </div>

          {gameState.gameOver && (
            <div className="game-over">
              <h2>GAME OVER</h2>
              <div>
                {gameState.team1Score > gameState.team2Score ? 'TOKYO Wins!' :
                gameState.team2Score > gameState.team1Score ? 'LA Wins!' :
                'Draw!'}
              </div>
              <div>{gameState.team1Score}% vs {gameState.team2Score}%</div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export function HotspotsOverlay({ onPOVClick }) {
  const [activeView, setActiveView] = useState('default');

  const handleSpotClick = (povKey) => {
    onPOVClick(povKey);
    setActiveView(povKey);
  };

  return (
    <div className="hotspot-container">
      {activeView === 'default' && (
        <>
          <div
            className="hotspot new-york"
            style={{
              left: '37%',
              bottom: '50%'
            }}
            onClick={() => handleSpotClick('newYork')}
          >
            <div className="hotspot-circle"></div>
            <div className="hotspot-circle-inner"></div>
            <div className="hotspot-label">LA VIEW</div>
          </div>

          <div
            className="hotspot tokyo"
            style={{
              right: '37%',
              bottom: '50%'
            }}
            onClick={() => handleSpotClick('tokyo')}
          >
            <div className="hotspot-circle"></div>
            <div className="hotspot-circle-inner"></div>
            <div className="hotspot-label">TOKYO VIEW</div>
          </div>
        </>
      )}

      {activeView !== 'default' && (
        <button
          className="reset-view-button"
          onClick={() => {
            onPOVClick('reset');
            setActiveView('default');
          }}
        >
          RESET VIEW
        </button>
      )}
    </div>
  );
}

export function Hotspots({ onPOVClick, currentView, placeShape, gameState }) {
  const handleSpotClick = (povKey) => {
    onPOVClick(povKey);
  };

  const handlePlaceShape = (team) => {
    // Only allow placing if it's this team's turn
    if (gameState && !gameState.gameOver && placeShape && gameState.currentTeam === team) {
      placeShape(Math.floor(Math.random() * 12), team);
    }
  };

  const shouldShowHotspots = currentView === 'reset';

  if (!shouldShowHotspots) return null;

  // Only allow placing for the current team's turn
  // Team 1 = Tokyo (negative X), Team 2 = LA (positive X)
  const isTokyoTurn = gameState && gameState.currentTeam === 1;
  const isLATurn = gameState && gameState.currentTeam === 2;
  const canPlaceTokyo = gameState && !gameState.gameOver && gameState.team1Moves > 0 && isTokyoTurn;
  const canPlaceLA = gameState && !gameState.gameOver && gameState.team2Moves > 0 && isLATurn;

  return (
    <>
      {/* TOKYO SIDE (negative X) - View button (back) */}
      <mesh position={[-65, 20, 0]}>
        <sphereGeometry args={[0.6, 24, 24]} />
        <meshBasicMaterial visible={false} />
        <Html
          position={[0, 2.2, 0]}
          className="hotspot-3d-container"
          center
          distanceFactor={30}
        >
          <div
            className="hotspot view-hotspot"
            onClick={() => handleSpotClick('tokyo')}
            role="button"
            tabIndex={0}
          >
            <div className="hotspot-circle"></div>
            <div className="hotspot-circle-inner"></div>
            <div className="hotspot-label">TOKYO VIEW</div>
          </div>
        </Html>
      </mesh>

      {/* TOKYO SIDE (negative X) - Place Shape button (front, toward center) */}
      <mesh position={[-45, 20, 0]}>
        <sphereGeometry args={[0.6, 24, 24]} />
        <meshBasicMaterial visible={false} />
        <Html
          position={[0, 2.2, 0]}
          className="hotspot-3d-container"
          center
          distanceFactor={30}
        >
          <div
            className={`hotspot place-shape-hotspot tokyo-shape ${!canPlaceTokyo ? 'disabled' : ''}`}
            onClick={() => canPlaceTokyo && handlePlaceShape(1)}
            role="button"
            tabIndex={0}
          >
            <div className="hotspot-circle large"></div>
            <div className="hotspot-circle-inner large"></div>
            <div className="hotspot-label">PLACE TOKYO SHAPE</div>
          </div>
        </Html>
      </mesh>

      {/* LA SIDE (positive X) - View button (back) */}
      <mesh position={[65, 20, 0]}>
        <sphereGeometry args={[0.6, 24, 24]} />
        <meshBasicMaterial visible={false} />
        <Html
          position={[0, 2.2, 0]}
          className="hotspot-3d-container"
          center
          distanceFactor={30}
        >
          <div
            className="hotspot view-hotspot"
            onClick={() => handleSpotClick('newYork')}
            role="button"
            tabIndex={0}
          >
            <div className="hotspot-circle"></div>
            <div className="hotspot-circle-inner"></div>
            <div className="hotspot-label">LA VIEW</div>
          </div>
        </Html>
      </mesh>

      {/* LA SIDE (positive X) - Place Shape button (front, toward center) */}
      <mesh position={[45, 20, 0]}>
        <sphereGeometry args={[0.6, 24, 24]} />
        <meshBasicMaterial visible={false} />
        <Html
          position={[0, 2.2, 0]}
          className="hotspot-3d-container"
          center
          distanceFactor={30}
        >
          <div
            className={`hotspot place-shape-hotspot la-shape ${!canPlaceLA ? 'disabled' : ''}`}
            onClick={() => canPlaceLA && handlePlaceShape(2)}
            role="button"
            tabIndex={0}
          >
            <div className="hotspot-circle large"></div>
            <div className="hotspot-circle-inner large"></div>
            <div className="hotspot-label">PLACE LA SHAPE</div>
          </div>
        </Html>
      </mesh>
    </>
  );
}
